/**
 * 工具模块
 */
import buildUtils from 'build-util';
import MJSSDK from '@mfex/mjssdk';
import {AxiosPromise, AxiosResponse} from 'axios';
import conf from 'site-config';
import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';
import Vue from 'vue';
import URL from 'url';
import RequireContext = __WebpackModuleApi.RequireContext;

const isMamaApp = MJSSDK.UA.mamaApp;
// const isSupportWebp = isBrowserSupportWebp();
const isSupportWebp = false;
// const isWx = MJSSDK.UA.wx;

export const slash: (path: string) => string = path => {
    const isExtendedLengthPath = /^\\\\\?\\/.test(path);
    const hasNonAscii = /[^\u0000-\u0080]+/.test(path); // eslint-disable-line no-control-regex

    if (isExtendedLengthPath || hasNonAscii) {
        return path;
    }

    return path.replace(/\\/g, '/');
};

/**
 * 配置文档标题信息
 * @param {string} title
 */
export function setNavTitle(title: string): void {
    if (isMamaApp) {
        MJSSDK.setTitle({title});
    } else {
        document.title = title;
    }
}

export function noop(): void {
    return undefined;
}

/**
 * @default 处理后端的 "0" or "1" boolean 数据
 * @param value
 * @return {boolean}
 */
export function backendBoolean(value: string | number): boolean {
    return (typeof value === 'string' ? parseInt(value) : value) === 1;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface BackendResultData extends Record<string, any> {}

export class BackendResult<T = BackendResultData> {
    private static onErrorHooks: Array<(e: Error) => void> = [];
    private static trigErrorHooks = (e: Error) => {
        BackendResult.onErrorHooks.forEach(fn => {
            try {
                fn(e);
            } catch (err) {
                // ignore
            }
        });
    };
    public static setErrorHooks = (hook: (e: Error) => void) => {
        BackendResult.onErrorHooks.push(hook);
    };

    __raw: AxiosResponse;

    __result: {
        code: number;
        msg: string;
        data: T;
    };

    __status: string;

    constructor(result: {code: number; msg: string; data: T}) {
        const {code = -1, msg = '网络错误，请重新再试！', data = {}} = result || {};
        this.__result = {
            code,
            msg,
            data: data as T
        };
        this.__status = 'open';
    }

    static API = conf.API;

    static of<T = BackendResultData>(result): BackendResult<T> {
        return new BackendResult<T>(result);
    }

    static from<T = BackendResultData>(result: AxiosPromise): Promise<BackendResult<T>> {
        return result.then(
            raw => {
                const data = BackendResult.of<T>(raw.data);
                data.__raw = raw;
                return data;
            },
            e => {
                const data = BackendResult.of<T>({
                    // 内部错误
                    code: -2,
                    data: {
                        error: e
                    }
                });
                data.__raw = e;
                return data;
            }
        );
    }

    // stop(): this {
    //     this.__status = 'stop';
    //     return this;
    // }

    debug(): this {
        return this.forEach(item => {
            const titleCss =
                'font-weight: 500; background: #222; color: #fff; padding: 0 5px 0 10px; line-height: 24px;';
            const textCss = 'color: #fff; background: #222; padding: 0 5px 0 10px; line-height: 20px;';
            console.debug(
                `%cBackend Debug >>> \n${item.__raw.config.method.toUpperCase()} ${item.__raw.config.url}`,
                titleCss
            );
            console.debug('%cStatus code:', textCss, item.__result.code);
            console.debug('%cMsg:', textCss, item.__result.msg);
            console.debug('%cData:', textCss, item.__result.data);
            console.debug('%cRaw respond:', textCss, item.__raw);
            console.debug('%cParams:', titleCss, item.__raw.config.params);
            if (item.__raw.config.method === 'post') {
                console.debug('%cPost data:', titleCss, item.__raw.config.data);
            }
        });
    }

    forEach(fn: (item: BackendResult<T>) => void): this {
        fn(this);
        return this;
    }

    map<R = unknown>(fn: (item: BackendResult<T>) => R): R {
        return fn(this);
    }

    when(code: number, fn: (data: T, msg?: string) => void): this {
        // if (this.__status === 'stop') return this;
        if (this.__result.code === code && typeof fn === 'function') {
            fn(this.__result.data, this.__result.msg);
        }

        return this;
    }

    success(fn: (data: T) => void): this {
        try {
            return this.when(0, fn);
        } catch (e) {
            this.__result = {
                code: -1,
                msg: e.message,
                data: e
            };
            BackendResult.trigErrorHooks(e);
            return this;
        }
    }

    error(fn: (code: number, msg?: string, data?: T) => void): this {
        // if (this.__status === 'stop') return this;
        if (this.__result.code !== 0 && typeof fn === 'function') {
            const {code, msg, data} = this.__result;
            fn(code, msg, data);
        }
        return this;
    }
    // eslint-disable-next-line  @typescript-eslint/no-shadow
    promise<T>(fn: (br: this, res: (r: T) => void, rej: (e: Error) => void) => void): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            fn(this, resolve, reject);
        });
    }
}

BackendResult.setErrorHooks(e => {
    console.error('Error happened on success hook.');
    console.error(e);
});

export function tranformMaiUiName(obj: Record<string, unknown>): Record<string, unknown> {
    const _obj = {};
    for (const key in obj) {
        _obj[`Mai${key}`] = obj[key];
    }
    return _obj;
}

export function registerComponent(
    requireComponent: RequireContext,
    isRegisterComponentGlobally?: boolean
): Record<string, unknown> {
    const comp = {};

    requireComponent
        .keys()
        .map(slash)
        .forEach(fileName => {
            // 获取组件配置
            const componentConfig = requireComponent(fileName);

            // 取得组件的 Pascal 式命名
            const componentName = upperFirst(
                camelCase(
                    // 将文件名前面的 `./` 和扩展名剥离
                    fileName.replace(/^\.\/(.*)\.\w+$/, '$1')
                )
            );
            if (!isRegisterComponentGlobally) {
                // 以全局方式注册组件
                Vue.component(
                    componentName,
                    // 如果组件是通过 `export default` 导出，
                    // 则在 `.default` 中，查找组件选项，
                    // 否则回退至模块根对象中，查找组件选项
                    componentConfig.default || componentConfig
                );
            }

            comp[componentName] = componentConfig.default || componentConfig;
        });
    return comp;
}

// 获取用户设备号
export function getDeviceID(): Promise<string> {
    return new Promise(resolve => {
        MJSSDK.getUnifiedDeviceID({
            success: function (res) {
                resolve(res.deviceId);
            }
        });
    });
}

// 涉及用户隐私权限问题，不开启相关功能 知悉
// export function getClipboardData(): Promise<string> {
//     return new Promise(resolve => {
//         MJSSDK.invocation({
//             action: 'getClipboardData', // 功能，参考附录一
//             version: '1.0.0', // 版本，参考附录一，表无则表示非必需
//             data: {}, // 传值 APP [可选]，参考附录一
//             success: function (res) {
//                 // console.log(res);    // APP 返回数据
//                 resolve(res.value);
//             }
//         });
//     });
// }

/**
 * @description 判断是不是 q 协议
 * @author zhangxj
 * @date 2020-04-21
 * @export
 * @param {string} href
 * @returns {boolean} true href 是 q协议链接
 */
export function isBq(href: string): boolean {
    return href.toLowerCase().indexOf('q:') === 0;
}

/**
 * 获取图片的绝对路径
 * @param {string} relativePath 相对路径，以 "img" 或 "media" 开头，如"img/test.png"
 */
export function getAssetURL(relativePath) {
    return buildUtils.assetsURL(relativePath);
}

export function isBrowserSupportWebp(): boolean {
    return document.createElement('canvas').toDataURL('image/webp').includes('image/webp');
}

// 规则来源于淘宝 wap 商品详情 imgUrlParser 源码
export function tbImgUrlTranslator(imgUrl, webpWidth, webpHeight) {
    let r = '_';
    if (webpWidth > 0) {
        r += `${webpWidth}x${void 0 !== webpHeight ? webpHeight : webpWidth}`;
    }
    r += 'q80';
    if (isSupportWebp) {
        r += '_.webp';
    }
    return imgUrl + r;
}

/**
 * 传入宽高，等比缩放，裁剪图片正中部分
 * @param {String} imgUrl
 * @param {Number} w
 * @param {Number}h
 */
function resizeQiniuImage({imgUrl, w, h}) {
    if (!imgUrl || typeof imgUrl !== 'string') {
        return imgUrl;
    }
    if (imgUrl.includes('.xiaoshuxiong.com') && !imgUrl.includes('imageView2')) {
        // https://developer.qiniu.com/dora/api/basic-processing-images-imageview2
        const param = `imageView2/${w && h ? '1' : '0'}${w ? `/w/${w}` : ''}${h ? `/h/${h}` : ''}`;
        if (imgUrl.includes('?')) {
            return `${imgUrl}&${param}`;
        } else {
            return `${imgUrl}?${param}`;
        }
    } else {
        return imgUrl;
    }
}

// 七牛规则来源于 https://developer.qiniu.com/dora/api/1279/basic-processing-images-imageview2
export function qnImgUrlTranslator(imgUrl, w?, h?): string {
    let pre = imgUrl.indexOf('?') > -1 ? '&imageView2/2' : '?imageView2/2';
    pre = `${imgUrl.indexOf('imageView2/2') > -1 ? '' : pre}`;
    const url = `${imgUrl}${pre}/format/webp`;
    return resizeQiniuImage({imgUrl: isSupportWebp ? url : imgUrl, w, h});
}

// 图片 url 处理
export function imgUrlTranslator(imgUrl: string, w?: number, h?: number): string {
    // const availWidth = window.screen.availWidth; // 屏幕分辨率

    try {
        if (imgUrl.indexOf('data:') > -1) {
            return imgUrl;
        } else if (/(alicdn|taobaocdn).com/.test(imgUrl)) {
            return imgUrl;
            // if (imgUrl.match(/(.jpg|.jpeg|.png|.jpeg)/g).length > 1 || imgUrl.match('_b.jpg')) {
            //     return isSupportWebp ? `${imgUrl}_.webp` : imgUrl;
            // } else if (/\.gif/.test(imgUrl)) {
            //     return (imgUrl += '_640x0q80');
            // } else {
            //     const w = availWidth < 1080 ? 640 : 1080; // 淘宝图片按手机分辨率设定图片尺寸
            //     const h = availWidth < 1080 ? 0 : 1800; // 淘宝图片按手机分辨率设定图片尺寸
            //     return tbImgUrlTranslator(imgUrl, w, h); // 淘宝 wap 统一定义的规则
            // }
        } else if (/xiaoshuxiong.com/.test(imgUrl)) {
            return qnImgUrlTranslator(imgUrl, w, h);
        } else {
            return imgUrl;
        }
    } catch (e) {
        console.error(e);
    }
}

/**
 * 获得地址栏参数
 * @param  {string} url 地址
 * @return {Object} 参数对象
 */
export function getQueryParam(url?: string): {[key: string]: string} {
    return URL.parse(url || location.href, true).query as {[key: string]: string};
}

export function throttle<T extends (...args: Array<any>) => void>(fn: T, delay: number): (...args: Array<any>) => void {
    let startTime;
    return function (...args) {
        const currentTime = Number(new Date());
        if (!startTime) {
            startTime = currentTime;
        }
        if (currentTime - startTime > delay) {
            startTime = currentTime;
            fn.apply(this, args);
        }
    };
}

type ThrottleStoresArg<T> = T extends (storeValue: Array<infer R>) => void ? R : void;

/**
 * @description 相当于一个可以临时帮你把调用过的参数都存起来的 throttle，用于批处理上报操作
 * @param {Function} fn
 * @param {number} delay
 * @param {number} maxTimeLimit - 最大限制时间后必须发送
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function throttleWithStores<T extends (storeValues: Array<any>) => void>(
    fn: T,
    delay: number,
    maxTimeLimit = 1000
): (storeValue: ThrottleStoresArg<T>) => void {
    let startTime;
    let internalStore: Array<ThrottleStoresArg<T>> = [];
    let timeoutHandler = null;
    return storeValue => {
        internalStore.push(storeValue as ThrottleStoresArg<T>);
        const currentTime = Number(new Date());
        // eslint-disable-next-line  @typescript-eslint/no-shadow
        const clearTimeoutHandler = timeoutHandler => {
            if (timeoutHandler) {
                clearTimeout(timeoutHandler);
            }
        };

        const apply = () => {
            clearTimeoutHandler(timeoutHandler);
            startTime = currentTime;
            const args = internalStore.filter(item => item);
            if (args.length > 0) {
                fn.apply(this, [args]);
                internalStore = [];
            }
        };

        if (maxTimeLimit > 0) {
            clearTimeoutHandler(timeoutHandler);
            timeoutHandler = setTimeout(apply, maxTimeLimit);
        }

        if (!startTime) {
            startTime = currentTime;
        }
        if (currentTime - startTime > delay) {
            apply();
        }
    };
}

/**
 * @description 检查手机是否有安装拼多多 app ，并设置缓存结果
 */
export function checkHasPddAppAndSetLocalStorage(): Promise<string> {
    return new Promise(resolve => {
        MJSSDK.hasApp({
            packages: 'com.xunmeng.pinduoduo',
            scheme: 'pinduoduo://',
            success: function (res) {
                if (res.isInstall === 1) {
                    // 1 有安装 0 无安装
                    localStorage.setItem('isInstallPdd', 'Y');
                } else {
                    localStorage.setItem('isInstallPdd', 'N');
                }
                resolve(String(res.isInstall));
            }
        });
    });
}

export function isNumber(val: string | number): boolean {
    if (typeof val === 'number') {
        return !isNaN(val);
    }
    const ex = /^\d+(.\d+)?$/;
    return ex.test(val);
}

export function scrollTo(el: Element, x: number, y: number) {
    if (typeof el?.scrollTo === 'function') {
        el.scrollTo(x, y);
    } else {
        el.scrollTop = x;
        el.scrollLeft = y;
    }
}
